/**
 * Epic AB#1824 — Business Hours Matrix (Model E) TanStack Query hooks.
 *
 * The matrix collapses two legacy editors (BusinessHours UI + per-branch
 * BranchHours editor on the Branch card) into a single source-of-truth
 * editor on `/my-business/business-details` > Hours. Reads
 * `GET /api/business/hours/effective` and writes via the transactional
 * `PUT /api/business/hours/bulk` endpoint (Faris brief §3.1 + §3.2).
 *
 * Concurrency model — see Faris §2.7 + Dina §2.7:
 *   - Each cell carries an `expectedUpdatedAt` epoch ms token in the PUT.
 *   - Server returns `409 Hours.Stale` with `details.conflictedCells` when
 *     any cell's expected token disagrees with the most-recent audit row.
 *   - The hook surfaces the 409 envelope as a typed error so the matrix
 *     state machine can switch to the `ConcurrencyBanner`.
 *
 * Idempotency — see Faris §Decision 8:
 *   - The matrix mounts a UUID v4 idempotency key + persists to
 *     `sessionStorage` under `idempKey:hours-matrix:<businessId>`.
 *   - On mutation success the key is cleared so the next bulk-apply uses a
 *     fresh key (preventing inadvertent cross-request dedup).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { apiService } from '../services/api';
import { queryKeys } from '../lib/queryKeys';
import type {
    EffectiveBranchHoursResponse,
    HoursStaleErrorDetails,
    SetBusinessHoursBulkRequest,
    SetBusinessHoursBulkResponse,
} from '../types/api-responses';

/* ── Day-of-week normalization (mirrors useBranchHours.ts) ────────────── */
const DAY_NAME_TO_INDEX: Record<string, number> = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6,
};

function normalizeDayOfWeek(dow: number | string): number {
    if (typeof dow === 'number') return dow;
    const idx = DAY_NAME_TO_INDEX[dow];
    return typeof idx === 'number' ? idx : -1;
}

function normalizeEffectiveResponse(
    resp: EffectiveBranchHoursResponse,
): EffectiveBranchHoursResponse {
    const normalizeHours = <
        T extends { dayOfWeek: number | string },
    >(rows: T[]): T[] => {
        return (rows ?? []).map((h) => ({
            ...h,
            dayOfWeek: normalizeDayOfWeek(h.dayOfWeek),
        })).sort((a, b) => a.dayOfWeek - b.dayOfWeek);
    };
    return {
        ...resp,
        branches: (resp.branches ?? []).map((b) => ({
            ...b,
            hours: normalizeHours(b.hours),
        })),
        defaultRow: {
            hours: normalizeHours(resp.defaultRow?.hours ?? []),
        },
    };
}

/* ── Idempotency-Key persistence ──────────────────────────────────────── */
const idempKeyStorageKey = (businessId: number | null | undefined): string =>
    `idempKey:hours-matrix:${businessId ?? 'unknown'}`;

function newUuidV4(): string {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) return c.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
        const r = (Math.random() * 16) | 0;
        const v = ch === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Reads the cached idempotency key from sessionStorage, or mints + persists
 * a fresh one. The key survives in-page retries (network blips) but is
 * cleared on successful save so the NEXT bulk-apply gets a new key.
 */
function getOrCreateIdempKey(businessId: number | null | undefined): string {
    const key = idempKeyStorageKey(businessId);
    try {
        const existing = sessionStorage.getItem(key);
        if (existing) return existing;
    } catch {
        /* sessionStorage unavailable — fall through to fresh mint */
    }
    const fresh = newUuidV4();
    try {
        sessionStorage.setItem(key, fresh);
    } catch {
        /* ignore */
    }
    return fresh;
}

function clearIdempKey(businessId: number | null | undefined): void {
    try {
        sessionStorage.removeItem(idempKeyStorageKey(businessId));
    } catch {
        /* ignore */
    }
}

/* ── Typed error envelope for 409 Hours.Stale ─────────────────────────── */
export interface HoursStaleError extends Error {
    code: 'Hours.Stale';
    details: HoursStaleErrorDetails;
}

export function isHoursStaleError(err: unknown): err is HoursStaleError {
    return (
        typeof err === 'object'
        && err !== null
        && (err as { code?: string }).code === 'Hours.Stale'
    );
}

/**
 * GET /api/business/hours/effective — matrix-shaped read. Single tenant-
 * scoped cache key. Stale time mirrors other settings queries so the matrix
 * re-fetches when the user navigates away and back.
 */
export const useEffectiveBranchHoursQuery = (enabled = true) =>
    useQuery<EffectiveBranchHoursResponse>({
        queryKey: queryKeys.business.hoursEffective(),
        queryFn: () =>
            apiService.getEffectiveBranchHours().then(normalizeEffectiveResponse),
        enabled,
        staleTime: 30_000, // 30s — matches other tenant-config queries
    });

/**
 * PUT /api/business/hours/bulk — transactional bulk-write.
 *
 * The mutation handles three concerns the matrix shouldn't see:
 *   1. Idempotency-Key generation + sessionStorage persistence
 *   2. 409 Hours.Stale envelope → typed `HoursStaleError` rethrow
 *   3. Effective-hours cache invalidation on success + per-branch hours
 *      cache invalidation so the legacy BranchHoursModal (still in the
 *      bundle until decommissioned) doesn't go stale either.
 */
export const useSetBusinessHoursBulkMutation = (
    businessId: number | null | undefined,
) => {
    const queryClient = useQueryClient();
    return useMutation<
        SetBusinessHoursBulkResponse,
        Error | HoursStaleError,
        SetBusinessHoursBulkRequest
    >({
        mutationFn: async (request) => {
            const idempKey = getOrCreateIdempKey(businessId);
            try {
                return await apiService.setBusinessHoursBulk(request, idempKey);
            } catch (err) {
                const axiosErr = err as AxiosError<{
                    errors?: Array<{
                        code: string;
                        message: string;
                        details?: HoursStaleErrorDetails;
                    }>;
                }>;
                // 409 → wrap as typed HoursStaleError so callers can branch
                // on the discriminator without poking at the axios shape.
                const status = axiosErr.response?.status;
                const firstError = axiosErr.response?.data?.errors?.[0];
                if (status === 409 && firstError?.code === 'Hours.Stale') {
                    const stale: HoursStaleError = Object.assign(
                        new Error(firstError.message ?? 'Hours.Stale'),
                        {
                            code: 'Hours.Stale' as const,
                            details: firstError.details ?? { conflictedCells: [] },
                        },
                    );
                    throw stale;
                }
                throw err;
            }
        },
        onSuccess: () => {
            // Clear the persisted idempotency key — next save gets a fresh
            // one. Refetch the matrix shape so cells reflect server truth
            // (including the post-bulk Default-row recomputation).
            clearIdempKey(businessId);
            queryClient.invalidateQueries({
                queryKey: queryKeys.business.hoursEffective(),
            });
            // Bust the legacy per-branch cache too — the BranchHoursModal
            // is decommissioned by this Epic but the legacy hook stays in
            // the codebase for the mobile read path.
            queryClient.invalidateQueries({
                queryKey: queryKeys.branches.all,
            });
        },
    });
};
